import { OlympiadParticipation } from '@shared/ui'
import {
  OlympiadParticipantStatus,
  ParticipantTimingState,
  type ParticipantOlympiad,
  type ParticipantTiming,
} from './types'

/** Признаки карточки, которые бэк не отдаёт одним полем. */
export interface OlympiadCardFlags {
  /** Олимпиада идёт ИМЕННО ДЛЯ ЭТОГО участника. `undefined` — тайминга нет,
   *  решает строка статуса. */
  hasStarted?: boolean
  /** Уже закончилась. */
  hasEnded?: boolean
  /** Записаться можно. `false` — бэк отказал или регистрация закрыта;
   *  `undefined` — расписания нет, судить не по чему. */
  canRegister?: boolean
  /** Дисквалификация или отмена регистрации — своим бейджем карточки. */
  participation?: OlympiadParticipation
}

/** Завершение — терминальное состояние. Дисквалификация сюда не входит: она
 *  терминальна для участия, но «закончилась» ли олимпиада, решают даты
 *  (`isParticipantWindowOver`), иначе карточка пишет «Завершено» за дни до старта. */
const FINISHED_STATES: readonly ParticipantTimingState[] = [ParticipantTimingState.finished]

/** Дата в прошлом? Дни приходят без времени — сравниваем по концу суток,
 *  иначе олимпиада «заканчивается» в свою же полночь. */
function isPastDay(day: string | null | undefined, nowMs: number): boolean {
  if (!day) return false
  const end = new Date(`${day}T23:59:59`)
  return !Number.isNaN(end.getTime()) && end.getTime() < nowMs
}

/**
 * Последний известный день олимпиады уже прошёл.
 *
 * Смотрит и на `last_end_date`, и на этапы: в списке участника дат этапов нет,
 * а в детальном ответе, наоборот, `last_end_date` бывает пустым при живых
 * этапах (олимпиада 94 на стенде 2026-08-22).
 */
export function isOlympiadPastByDates(
  olympiad: Pick<ParticipantOlympiad, 'last_end_date' | 'stages'>,
  nowMs: number
): boolean {
  if (isPastDay(olympiad.last_end_date, nowMs)) return true
  const stages = olympiad.stages ?? []
  if (stages.length === 0) return false
  return stages.every((stage) => isPastDay(stage.end_date, nowMs))
}

/** Конец личного окна в мс; `null`, если его нет или дату не разобрать. */
function participantEndMs(
  olympiad: Pick<ParticipantOlympiad, 'participant_timing'>
): number | null {
  const endAt = olympiad.participant_timing?.end_at
  if (!endAt) return null
  const endMs = new Date(endAt).getTime()
  return Number.isNaN(endMs) ? null : endMs
}

/**
 * Олимпиада для этого участника закончилась — по датам, а не по состоянию.
 *
 * Дисквалификация терминальна и сама по себе не говорит, прошла ли олимпиада:
 * «дисквалифицирован за дни до старта» и «олимпиада прошла» различает только
 * дата. Здесь: личное окно `participant_timing.end_at` уже закрылось (`nowMs` —
 * серверные часы), а если окна нет — страховка `isOlympiadPastByDates`. Одно
 * правило для карточки списка (`olympiadCardFlags`) и кнопки результата.
 */
export function isParticipantWindowOver(
  olympiad: Pick<ParticipantOlympiad, 'participant_timing' | 'last_end_date' | 'stages'>,
  nowMs: number
): boolean {
  const endMs = participantEndMs(olympiad)
  if (endMs !== null) return endMs <= nowMs
  return isOlympiadPastByDates(olympiad, nowMs)
}

/**
 * Состояние карточки по данным участника, а не по строке `status`.
 *
 * `status` живёт на уровне олимпиады и под участника не пересчитывается:
 * на стенде 2026-08-22 у олимпиады 169 он говорил «Идет сейчас», хотя старт
 * участника — через шесть дней, и карточка предлагала «Начать олимпиаду»
 * рядом с обратным отсчётом (замечание владельца, скриншот 7). Личное
 * состояние `participant_state` / `participant_timing.state` знает правду.
 *
 * Страховка по датам — на случай, когда бэк держит «Идет сейчас» у олимпиады,
 * чей последний этап уже прошёл. Идущему участию она НЕ мешает: пока бэк
 * говорит `in_progress`, окно открыто, и снимать его по своим часам нельзя —
 * иначе участник, у которого олимпиада в самом разгаре, увидит «Завершена».
 *
 * У олимпиады 94 страховка не срабатывает вовсе: в списке по ней нет ни дат,
 * ни состояния (`first_start_date`, `last_end_date`, `time_left`,
 * `participant_state` — всё `null`), и просроченность фронту взять неоткуда.
 * Это вопрос к бэку, а не недоделка карточки.
 */
export function olympiadCardFlags(
  olympiad: Pick<
    ParticipantOlympiad,
    | 'participant_state'
    | 'participant_timing'
    | 'participant_registration'
    | 'last_end_date'
    | 'stages'
  >,
  nowMs: number
): OlympiadCardFlags {
  const state = olympiad.participant_state ?? olympiad.participant_timing?.state
  const canRegister = resolveCanRegister(olympiad.participant_timing)
  const resolved = resolveParticipation(olympiad, state, canRegister)
  const participation =
    resolved === OlympiadParticipation.cancelled && isCancelledAfterEnd(olympiad, nowMs)
      ? undefined
      : resolved

  if (!state) {
    return { hasEnded: isOlympiadPastByDates(olympiad, nowMs), canRegister, participation }
  }

  if (state === ParticipantTimingState.disqualified) {
    return {
      hasStarted: false,
      hasEnded: isParticipantWindowOver(olympiad, nowMs),
      canRegister,
      participation,
    }
  }

  return {
    hasStarted: state === ParticipantTimingState.inProgress,
    hasEnded: hasEndedForState(olympiad, state, nowMs),
    canRegister,
    participation,
  }
}

/** Закончилась ли олимпиада для участника с известным состоянием (не
 *  дисквалификацией): терминальное `finished`, либо не идёт и даты прошли. */
function hasEndedForState(
  olympiad: Pick<ParticipantOlympiad, 'last_end_date' | 'stages'>,
  state: ParticipantTimingState,
  nowMs: number
): boolean {
  if (FINISHED_STATES.includes(state)) return true
  return state !== ParticipantTimingState.inProgress && isOlympiadPastByDates(olympiad, nowMs)
}

/**
 * Отменённая регистрация на уже закончившейся олимпиаде: и карточка, и
 * страница олимпиады показывают её как завершённую (карточка — без бейджа
 * отмены, страница — панелью «Завершено» с причиной). Оба зовут эту функцию —
 * единственное место, где это решается, при любой форме `participant_timing`: полной, без `state` и
 * `null` — тогда «закончилась» значит «прошли даты».
 */
export function isCancelledAfterEnd(
  olympiad: Pick<
    ParticipantOlympiad,
    | 'participant_state'
    | 'participant_timing'
    | 'participant_registration'
    | 'last_end_date'
    | 'stages'
  >,
  nowMs: number
): boolean {
  if (olympiad.participant_registration?.status !== OlympiadParticipantStatus.cancelled) {
    return false
  }
  const state = olympiad.participant_state ?? olympiad.participant_timing?.state
  if (!state) return isOlympiadPastByDates(olympiad, nowMs)
  if (state === ParticipantTimingState.disqualified) return false
  return hasEndedForState(olympiad, state, nowMs)
}

/**
 * Дисквалификация — всегда своим бейджем. Отмена — только если записаться
 * заново нельзя: при открытой записи карточка снова зовёт «Принять участие»
 * (`canRegister === true`), а не оставляет участника без действия.
 */
function resolveParticipation(
  olympiad: Pick<ParticipantOlympiad, 'participant_registration'>,
  state: ParticipantTimingState | null | undefined,
  canRegister: boolean | undefined
): OlympiadParticipation | undefined {
  if (
    state === ParticipantTimingState.disqualified ||
    olympiad.participant_registration?.status === OlympiadParticipantStatus.disqualified
  )
    return OlympiadParticipation.disqualified
  if (olympiad.participant_registration?.status === OlympiadParticipantStatus.cancelled)
    return canRegister === true ? undefined : OlympiadParticipation.cancelled
  return undefined
}

/**
 * Можно ли записаться.
 *
 * Случая два, и оба про «нельзя»: отказное расписание (`available: false` —
 * у олимпиады 94 бэк отвечает `offline_venue_not_found`, у 154 —
 * `grade_slot_not_found`) и обычное `can_register: false`, то есть регистрация
 * закрыта или ещё не открылась (олимпиады 103 и 104 на стенде). В обоих
 * карточка предлагала «Принять участие» (найдено рантайм-проверкой
 * 2026-08-22). Кнопка не должна звать туда, куда записаться нельзя.
 */
function resolveCanRegister(timing: ParticipantTiming | null | undefined): boolean | undefined {
  if (!timing) return undefined
  if (!timing.available) return false
  return timing.can_register
}

/** Бэк не предлагает участнику ни одного действия.
 *
 *  `can_onboard` входит в набор наравне с остальными: он открывается за 15
 *  минут до старта, когда `can_start` ещё `false`. Без него олимпиада, в
 *  которую прямо сейчас можно войти, считалась бы «действий нет» — и
 *  страховка от просроченности (`timingState`) объявила бы её завершённой. */
export function hasNoTimingActions(timing: ParticipantTiming | null | undefined): boolean {
  if (!timing) return true
  return (
    !timing.can_register &&
    !timing.can_start &&
    !timing.can_edit_registration &&
    !timing.can_onboard
  )
}

/**
 * Момент старта олимпиады для участника: личный приоритетнее общего — его могли
 * перенести в другой слот. Один источник для карточки списка, перезапроса списка
 * (`nearestFutureMoment`) и баннера главной, чтобы они не расходились.
 */
export function participantStartAt(
  olympiad: Pick<ParticipantOlympiad, 'start_at' | 'participant_timing'>
): string | null {
  return olympiad.participant_timing?.start_at ?? olympiad.start_at ?? null
}

/** Сутки в мс — одна константа на окна, плашку и порог «за сутки до старта». */
export const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Личный старт, а если момента нет (старый ответ) — день `first_start_date`
 * без времени. Один источник для окон при входе и плашки главной.
 */
export function participantStartOrDay(
  olympiad: Pick<ParticipantOlympiad, 'start_at' | 'participant_timing' | 'first_start_date'>
): string | null {
  return participantStartAt(olympiad) ?? olympiad.first_start_date ?? null
}

/**
 * Ближайший будущий момент, когда состояние карточек списка сменится само, —
 * старт или конец окна участника.
 *
 * Бэк переводит участника из «зарегистрирован» в «идёт» и дальше по
 * расписанию, а список лежит в кеше. К этому моменту список перечитывают,
 * иначе карточка показывает прошлое состояние до перезагрузки страницы.
 * `nowMs` — момент получения данных: от него и считается «будущее».
 */
export function nearestFutureMoment(
  olympiads: readonly ParticipantOlympiad[],
  nowMs: number,
  /** Ещё и «старт минус столько» — порог, за которым у плашки главной
   *  появляется «завтра». У списка олимпиад такого порога нет. */
  leadMs = 0
): string | null {
  const moments: { iso: string; ms: number }[] = []
  const consider = (iso: string, ms: number) => {
    if (!Number.isNaN(ms) && ms > nowMs) moments.push({ iso, ms })
  }
  for (const olympiad of olympiads) {
    const timing = olympiad.participant_timing
    const startIso = participantStartAt(olympiad)
    if (startIso && leadMs > 0) {
      const leadMoment = new Date(new Date(startIso).getTime() - leadMs)
      if (!Number.isNaN(leadMoment.getTime()))
        consider(leadMoment.toISOString(), leadMoment.getTime())
    }
    for (const iso of [startIso, timing?.end_at]) {
      if (iso) consider(iso, new Date(iso).getTime())
    }
  }
  moments.sort((a, b) => a.ms - b.ms)
  return moments[0]?.iso ?? null
}

/** За сколько до личного старта у плашки главной появляется «завтра». */
export const BEFORE_START_WINDOW_MS = DAY_MS

/**
 * Старт участника ещё впереди, но не дальше чем через сутки. Старт уже
 * наступил — это «идёт» или «прошло», а не «скоро».
 */
export function isStartingWithinDay(
  olympiad: Pick<ParticipantOlympiad, 'start_at' | 'participant_timing'>,
  nowMs: number
): boolean {
  const startAt = participantStartAt(olympiad)
  if (!startAt) return false
  const left = new Date(startAt).getTime() - nowMs
  return left > 0 && left <= BEFORE_START_WINDOW_MS
}

/**
 * Олимпиада идёт для этого участника: бэк говорит `in_progress` либо личное
 * окно `start_at…end_at` накрывает «сейчас». Окно закончилось по серверным
 * часам — не идёт, даже если строка состояния в кеше ещё старая.
 */
export function isParticipantOngoing(
  olympiad: Pick<
    ParticipantOlympiad,
    | 'start_at'
    | 'participant_state'
    | 'participant_timing'
    | 'participant_registration'
    | 'last_end_date'
    | 'stages'
  >,
  nowMs: number
): boolean {
  const flags = olympiadCardFlags(olympiad, nowMs)
  if (flags.participation === OlympiadParticipation.disqualified) return false
  if (flags.hasEnded) return false
  const endMs = participantEndMs(olympiad)
  if (endMs !== null && endMs <= nowMs) return false
  if (flags.hasStarted) return true
  const startAt = participantStartAt(olympiad)
  if (!startAt || endMs === null) return false
  return new Date(startAt).getTime() <= nowMs
}
